<script setup>
const { transitionColour } = useTailwindConfig();
const { currentRoute } = useRouter();
const client = useSupabaseClient();
const user = useState('user');
const municipality = useCookie('municipality');
const loading = ref(false);

const openFeedbackModal = useState('openFeedbackModal');

const notifications = useState('notifications', () => []);

const newNotificationCount = computed(() => {
  return notifications.value ? notifications.value.filter(n => !n.is_read).length : 0;
})

const useNonAdminView = useCookie('useNonAdminView');
if (useNonAdminView.value == null) {
  useNonAdminView.value = false;
}

const usePlannerView = useCookie('usePlannerView');
if (usePlannerView.value == null) {
  usePlannerView.value = true;
}

const supportedMunicipalityLogos = [
  'mm',
  'crowsnest',
];

const useMunicipalityLogo = computed(() => {
  if (user.value?.subscription != 'builder' && user.value?.use_municipality_branding && municipality.value && supportedMunicipalityLogos.includes(municipality.value)) {
    return true;
  }
  return false;
});

// Sign out handler
async function signOut() {
  loading.value = true;
  const { error } = await client.auth.signOut();

  // Delete the profileData cookie too (the one we set in default.vue to save on API calls)
  const profileDataCookie = useCookie('profileData');
  profileDataCookie.value = null;

  if (error) {
    console.log('There was an error signing out: ' + error.error_description || error.message);
  }
  else {
    // Using navigateTo('/') doesn't work sometimes for some reason
    window.location.href = '/';
  }
}

function closeDropdownAndNavigateTo(url, dropdownId, event) {
  // Close the dropdown before navigating
  const dropdown = document.getElementById(dropdownId);
  dropdown.click();

  // If ctrl key is pressed, open in new tab
  if (event?.ctrlKey) {
    window.open(url, '_blank');
  } else {
    navigateTo(url);
  }
}

async function fetchNotifications(){
  // Fetch notifications belonging to the user or non-user specific notifications
  const { data, error } = await client
    .schema(municipality.value)
    .from('notifications')
    .select('id, notification, is_read, created_at, inquiry_submission_id')
    .eq('profile_id', user.value.id)
    .order('created_at', { ascending: false });
  if(error){
    return;
  }
  if(data){
    notifications.value = data;
  }
  else{
    notifications.value = [];
  }

  // Set up realtime subscription
  const channel = client
    .channel('notification-changes')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: municipality.value,
        table: 'notifications',
        filter: `profile_id=eq.${user.value.id}`,
      },
      (payload) => {
        loading.value = true;
        // Handle insert
        if (payload.eventType === 'INSERT') {
          const newNotification = payload.new;
          const newNotificationObj = {
            id: newNotification.id,
            notification: newNotification.notification,
            is_read: newNotification.is_read,
            created_at: newNotification.created_at,
            inquiry_submission_id: newNotification.inquiry_submission_id,
          }
          notifications.value.unshift(newNotificationObj);
        }
        loading.value = false;
      }
    )
    .subscribe();
  
  // Store the channel for cleanup
  notificationsChannel.value = channel;
}

// Add this ref to store the channel
const notificationsChannel = ref(null);

// Clean up subscription on component unmount
onUnmounted(() => {
  if (notificationsChannel.value) {
    client.removeChannel(notificationsChannel.value);
  }
});

async function markAllNotificationsRead() {
  if (notifications.value.length === 0) return;
  // Mark all notifications as read in Supabase
  const ids = notifications.value.filter(n => !n.is_read && n.id).map(n => n.id);
  if (ids.length === 0) return;
  await client
    .schema(municipality.value)
    .from('notifications')
    .update({ is_read: true })
    .in('id', ids);
  notifications.value.forEach(n => n.is_read = true);
  closeDropdown('notif-more');
}

function navigateToNotificationsPage(event, closeMore = false) {
  if(closeMore){
    closeDropdown('notif-more');
  }
  closeDropdownAndNavigateTo('/notifications', 'notifications-dropdown', event);
  return;
}

function closeDropdown(dropdownId){
  const dropdown = document.getElementById(dropdownId);
  dropdown.click();
}

function goToInquirySubmission(inquirySubmissionId, event){
  closeDropdownAndNavigateTo(`/inquiry-report/result/${inquirySubmissionId}`, 'notifications-dropdown', event);
}

async function markNotificationAsRead(notificationId){
  if(notifications.value.find(n => n.id === notificationId).is_read){
    return;
  }
  await client
    .schema(municipality.value)
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);
  notifications.value.find(n => n.id === notificationId).is_read = true;
}

function daysAgo(dateStr) {
  const now = new Date();
  const notifDate = new Date(dateStr);
  const diffMs = now - notifDate;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    if( (diffMs / (1000 * 60)) < 1){
        return 'Now';
    }
    else if((diffMs / (1000 * 60)) < 60){
        const minutes = Math.floor(diffMs / (1000 * 60));
        return `${minutes}m`;
    }
    else{
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        return `${hours}h`;
    }
  }
  return `${diffDays}d`;
}

onMounted(async () => {
  await fetchNotifications();
});

</script>

<template>
  <nav class="flex items-center bg-gray-100 sticky z-50 top-0 h-[70px] border-b border-gray-200">
    <div class="w-full px-3 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <div class="w-[800px] items-center flex space-x-4">
          <!-- Logo -->
          <NuxtLink to="/" class="p-0.5 hover:bg-gray-200 rounded-lg" :class="transitionColour">
            <!-- Siteability Logo -->
            <div class="h-14">
              <NuxtImg v-if="useMunicipalityLogo" :src="`/images/municipality-logos/${municipality}.png`"
                alt="Siteability" class="w-full h-full object-contain" />
              <NuxtImg v-else src="/images/logo.png" alt="Siteability" class="w-full h-full object-contain" />
            </div>
          </NuxtLink>

          <!-- Homeowner View Toggle-->
          <!-- Needs to be ClientOnly to prevent some hydration errors for some reason -->
          <ClientOnly v-if="user?.subscription == 'planner'">
            <UButton variant="ghost" @click="usePlannerView = !usePlannerView;" size="lg">
              Switch to {{ usePlannerView ? 'Resident' : 'Planner' }} View
              <UIcon name="material-symbols:swap-horiz" class="min-w-4 min-h-4" />
            </UButton>
          </ClientOnly>
        </div>

        <!-- Search Bar (only show on report pages or sample user) -->
        <SearchBar v-if="user && (currentRoute.path.includes('report/') || showPlannerView())" type="navbar"
          class="hidden lg:flex grow px-4 max-w-96 my-auto items-center" />

        <!-- Right side buttons (logged in) -->
        <div v-if="user" class="w-[800px] flex justify-end items-center divide-x-2 divide-gray-700">
          <!-- Metric/Imperial Toggle -->
          <UnitSwitcher v-if="currentRoute.path.includes('report/')" />

          <!-- Inquire to City (homeowner only) -->
          <UButton v-if="!showPlannerView() && !showAdminView() && user?.subscription != 'builder'"
            class="mx-2 text-nowrap" size="lg" to="/inquire" icon="material-symbols:chat-outline-rounded">
            Inquire to City
          </UButton>

          <!-- Feedback -->
          <UButton v-if="user?.subscription != 'admin'" @click="openFeedbackModal.open()" class="mx-2 text-nowrap"
            size="lg" icon="material-symbols:bug-report-outline-rounded">
            Feedback
          </UButton>

          <!-- Notifications Dropdown -->
          <div v-if="user?.subscription === 'planner'" class="relative flex items-center">
            <UPopover mode="click" :content="{ side: 'bottom', align: 'end' }" :ui="{ content: 'rounded-lg' }">
              <button type="button" id="notifications-dropdown" 
                class="mx-2 text-nowrap flex items-center justify-center hover:bg-gray-200 rounded-lg p-1 relative text-gray-900" :class="transitionColour">
                <UIcon name="material-symbols:notifications-outline" size="27" />
                <span v-if="newNotificationCount > 0" class="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {{ newNotificationCount > 99 ? '99+' : newNotificationCount }}
                </span>
              </button>
              <template #content>
                <div class="min-w-[260px] p-4 text-black border-b-2 flex items-center justify-between">
                  <div class="font-semibold text-base">Notifications</div>
                  <UPopover mode="click" :content="{ side: 'bottom', align: 'end' }" :ui="{ content: 'rounded-lg' }">
                    <button type="button" id="notif-more" class="flex items-center justify-center hover:bg-gray-200 rounded-lg p-1 ml-2" :class="transitionColour">
                      <UIcon name="material-symbols:more-horiz" size="22" />
                    </button>
                    <template #content>
                      <div class="min-w-[160px] py-2 text-black">
                        <button v-if="newNotificationCount > 0" @click="markAllNotificationsRead" class="w-full text-left px-4 py-2 hover:bg-primary-500/10 text-gray-700 rounded-lg mb-1" :class="transitionColour">
                          Mark all as read
                        </button>
                        <button v-else class="w-full text-left px-4 py-2 text-gray-300 rounded-lg mb-1" :class="transitionColour">
                          Mark all as read
                        </button>
                        <button @click="(e) => navigateToNotificationsPage(e, true)" class="w-full text-left px-4 py-2 hover:bg-primary-500/10 text-gray-700 rounded-lg" :class="transitionColour">
                          Go to Notifications Page
                        </button>
                      </div>
                    </template>
                  </UPopover>
                </div>
                <div class="min-w-[260px] p-4 text-black">
                  <div v-if="!notifications" class="text-gray-500 text-sm py-2">No new notifications</div>
                  <div v-else>
                    <div v-for="notification in notifications.slice(0, 5)" :key="notification.id" class="py-1.5 text-sm w-full">
                      <button v-if="notification.inquiry_submission_id" @click="(e) => {markNotificationAsRead(notification.id); goToInquirySubmission(notification.inquiry_submission_id, e)}"
                        class="hover:bg-gray-50 rounded-lg p-2 py-4.5 border w-full flex justify-between items-center"
                        :class="{ 'border-l-4 border-l-primary-500': !notification.is_read}"
                        >
                        <span>{{ notification.notification }}</span>
                        <span class="pl-3 text-xs text-gray-400">{{ daysAgo(notification.created_at) }}</span>
                      </button>
                    </div>
                  </div>
                  <button v-if="notifications && notifications.length > 5" class="mt-3 w-full text-primary-500 font-semibold rounded-lg" @click="(e) => navigateToNotificationsPage(e)">
                    See all notifications
                  </button>
                </div>
              </template>
            </UPopover>
          </div>

          <div class="relative text-left flex my-auto">
            <UPopover mode="click" :content="{ side: 'bottom', align: 'start' }" :ui="{ content: 'rounded-lg' }">
              <button type="button" id="dropdown-button"
                class="sm:mr-3 text-gray-900 flex items-center hover:bg-gray-200 rounded-lg p-1 mx-2"
                :class="transitionColour">
                <UIcon name="material-symbols:person-outline" size="29" />
              </button>

              <template #content>
                <div class="text-lg text-black p-4 font-medium" :class="transitionColour">
                  <div class="flex justify-between items-start ">
                    <!-- Name -->
                    <span class="block text-sm text-black-900">
                      {{ user?.first_name }} {{ user?.last_name }}
                    </span>

                    <!-- Is Demo Badge -->
                    <UBadge v-if="user?.is_demo" label="DEMO" size="sm" color="error" />
                  </div>
                  <!-- Email -->
                  <span class="block text-sm text-gray-500 truncate">
                    {{ user?.email }}
                  </span>
                </div>
                <div class="rounded-b-lg text-sm divide-gray-300">
                  <hr class="border-gray-300" />
                  <button @click="(e) => closeDropdownAndNavigateTo('/', 'dropdown-button', e)"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="currentRoute.path == '/' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                    <UIcon name="material-symbols:search" class="h-5 w-5 mr-1" />
                    New Search
                  </button>

                  <button @click="(e) => closeDropdownAndNavigateTo('/search-history', 'dropdown-button', e)"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="currentRoute.path == '/search-history' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                    <UIcon name="material-symbols:history" class="h-5 w-5 mr-1" />
                    Search History
                  </button>

                  <button @click="(e) => closeDropdownAndNavigateTo('/assessment-history', 'dropdown-button', e)"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="currentRoute.path == '/assessment-history' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                    <UIcon name="material-symbols:edit-location-outline-rounded" class="h-5 w-5 mr-1" />
                    Assessment History
                  </button>

                  <hr class="border-gray-300" />
                  <button @click="(e) => closeDropdownAndNavigateTo('/users/settings', 'dropdown-button', e)"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="currentRoute.path == '/users/settings' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                    <UIcon name="material-symbols:settings" class="h-5 w-5 mr-1" />
                    Settings
                  </button>

                  <hr class="border-gray-300" />
                  <button @click="openFeedbackModal.open()"
                    class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="transitionColour">
                    <UIcon name="material-symbols:bug-report-outline-rounded" class="h-5 w-5 mr-1" />
                    Feedback & Bugs
                  </button>

                  <!-- Admin Only -->
                  <template v-if="user.subscription == 'admin'">
                    <hr class="border-gray-300" />
                    <div class="flex items-center pt-2 pl-2 pb-1 text-gray-400 font-medium">
                      <UIcon name="material-symbols:admin-panel-settings-outline-rounded" class="h-5 w-5 mr-1" />
                      <h4 class="text-sm">Admin Tools</h4>
                    </div>
                    <button @click="(e) => closeDropdownAndNavigateTo('/zoning-editor', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/zoning-editor' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Zoning Editor
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/modules-editor', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/modules-editor' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Modules Editor
                    </button>

                    <button
                      @click="(e) => closeDropdownAndNavigateTo('/zoning-editor/reverse-search', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/zoning-editor/reverse-search' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Reverse Search
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/users', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/users' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Users
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/blog', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/blog' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Blog
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/feedback', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/feedback' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Feedback Tracker
                    </button>

                    <button @click="(e) => closeDropdownAndNavigateTo('/demo', 'dropdown-button', e)"
                      class="flex items-center w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="currentRoute.path == '/demo' ? 'text-primary-500 bg-primary-500/10' : 'hover:text-primary-500 text-gray-700 hover:bg-primary-500/10', transitionColour">
                      Demo
                    </button>

                    <button @click="useNonAdminView = !useNonAdminView;"
                      class="flex items-center justify-between w-full px-3 py-2 cursor-pointer hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                      :class="transitionColour">
                      <span>Admin View</span>
                      <USwitch :model-value="!useNonAdminView" />
                    </button>
                  </template>

                  <!--Sign Out-->
                  <hr class="border-gray-300" />
                  <button
                    class="flex justify-between gap-2 w-full px-3 py-2 text-sm rounded-b-lg hover:text-primary-500 text-gray-700 hover:bg-primary-500/10"
                    :class="transitionColour" @click="signOut">
                    <div class="flex items-center">
                      <UIcon name="material-symbols:logout" class="h-5 w-5 mr-1" />
                      Sign out
                    </div>
                  </button>
                </div>
              </template>
            </UPopover>
          </div>
        </div>
      </div>
    </div>

    <!-- Absolutely Positioned Loading Spinner -->
    <div v-if="loading" class="fixed inset-0 bg-black/20 z-49 flex justify-center items-center">
      <LoadingSpinner class="w-14 h-14" />
    </div>
  </nav>
</template>